// eslint-disable
/* istanbul ignore file */
/** @jsx jsx */
import { Box, Button, Flex, Image } from '@chakra-ui/core';
import { motion } from 'framer-motion';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { jsx } from 'theme-ui';
import useAccountsWithLoading from '../../../app/hooks/useAccountsWithLoading';
import { useAuthenticationStatus } from '../../../app/hooks/useAuthenticationStatus';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { AuthenticationStatus } from '../../../app/models/appModel';
import logo from '../../../assets/img/AppyHere-Recruiter-Blue-Logo.png';
import appyHereLogo from '../../../assets/img/appyhere-logo.svg';
import {
  ANIMATED_VARIANTS,
  PATH_DASHBOARD,
  PATH_MAIN,
  PATH_SMB_BUSINESS_MANAGEMENT,
  PATH_SMB_LOGIN_QA,
} from '../../../routes/constants';
import { QASignUpView } from '../signup/SignUpView-QA-Env';
import { ActiveTabEnum } from '../userProfileInfoType';
import { CarouselView } from './CarouselView';
import { alertDialog, loginView, loginViewWrapper, smbLoginView } from './LoginView.styles';
import QALoginForm from './loginForm/LoginForm-QA-Env';
import { CustomAlert } from '../../common/CustomAlert/CustomAlert';
import { SeekerExistsInfoModal } from './SeekerExistsInfoModal';
import { AppLoadingSpinner } from '../../../app/components/AppLoadingSpinner';
import { useStoreState } from '../../../models/hooks';

const MotionBox = motion.custom(Box);

export const QALoginView: React.FC = () => {
  const { t } = useTranslation('login');
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<ActiveTabEnum>(ActiveTabEnum.LOGIN);
  const { currentUserProfile } = useCurrentUserProfile();
  const authenticationStatus = useAuthenticationStatus();
  const { account: currentUserAccount, loading: currentAccountLoading } = useAccountsWithLoading();
  const isExistingSeeker = useStoreState((state) => state.app.isExistingSeeker);
  const [isSeekerExistsModalOpen, setIsSeekerExistsModalOpen] = useState(false);
  const seekerFirstName = useStoreState((state) => state.app.seekerFirstName);

  const currentPath = location.pathname;

  useEffect(() => {
    if (authenticationStatus === AuthenticationStatus.NOT_AUTHENTICATED) {
      setActiveTab(ActiveTabEnum.LOGIN);
    }
  }, [authenticationStatus]);

  useEffect(() => {
    if (isExistingSeeker) {
      setIsSeekerExistsModalOpen(true);
    }
  }, [isExistingSeeker]);

  const getRedirectPath = (isDashboardEnabled?: boolean) => {
    if (currentPath === PATH_SMB_LOGIN_QA && activeTab === ActiveTabEnum.SIGN_UP) {
      return `${PATH_SMB_BUSINESS_MANAGEMENT}/create`;
    }
    if (isDashboardEnabled) {
      return PATH_DASHBOARD;
    }
    return PATH_MAIN;
  };

  if (currentAccountLoading) {
    return <AppLoadingSpinner />;
  }

  if (currentUserProfile && currentUserAccount && authenticationStatus !== AuthenticationStatus.NEW_USER) {
    return <Redirect to={getRedirectPath(currentUserAccount.configuration?.dashboardEnabled)} />;
  }

  const renderInvalidUserErrorMessage = () => {
    if (AuthenticationStatus.INVALID_USER === authenticationStatus) {
      return (
        <CustomAlert
          css={alertDialog}
          status="error"
          data-testid="InvalidUserMessage"
          height="100px"
          title={t('invalid_user_alert_title')}
          description={t('invalid_user_alert_description')}
        />
      );
    }
    return undefined;
  };

  const renderSeekerExistsMessage = () => {
    if (!isSeekerExistsModalOpen) {
      return undefined;
    }

    const heading = seekerFirstName
      ? t('seeker_exists_heading_with_name', { firstName: seekerFirstName })
      : t('seeker_exists_heading_without_name');
    const subheading = t('seeker_exists_subheading');

    const handleClose = () => setIsSeekerExistsModalOpen(false);

    const handleFindJob = () => {
      window.location.assign('https://appyhere-webapply-qa.web.app/');
    };

    const handleCreateRecruiterAccount = () => {
      handleClose();
    };

    return (
      <SeekerExistsInfoModal
        isOpen={isSeekerExistsModalOpen}
        onClose={handleClose}
        heading={heading}
        subheading={subheading}
        onFindJob={handleFindJob}
        onCreateRecruiterAccount={handleCreateRecruiterAccount}
      />
    );
  };

  return (
    <Fragment>
      <MotionBox
        css={loginViewWrapper}
        px={currentPath === PATH_SMB_LOGIN_QA ? 0 : '2vw'}
        initial="initial"
        animate="in"
        exit="out"
        variants={ANIMATED_VARIANTS}
      >
        <Flex
          justify="center"
          align="center"
          css={currentPath === PATH_SMB_LOGIN_QA ? smbLoginView : loginView}
          data-testid="LoginView"
        >
          <Flex
            bg="blue.30"
            rounded="lg"
            justifyContent="space-between"
            w={currentPath === PATH_SMB_LOGIN_QA ? '100%' : 'auto'}
            h={currentPath === PATH_SMB_LOGIN_QA ? '100%' : 'auto'}
          >
            {currentPath !== PATH_SMB_LOGIN_QA && (
              <Flex flex="0 0 35%" justify="center" alignItems="center" px={8}>
                <Image src={logo} alt="LogoImage" data-testid="LogoImage" />
              </Flex>
            )}
            <Flex
              alignItems="center"
              justifyContent="center"
              bg="white"
              roundedRight="lg"
              py={14}
              px={8}
              position="relative"
              width={currentPath === PATH_SMB_LOGIN_QA ? '35%' : 'auto'}
            >
              {currentPath === PATH_SMB_LOGIN_QA && (
                <Flex position="absolute" top="64px" left={0} width="100%" justifyContent="center">
                  <Image src={appyHereLogo} alt="Logo" data-testid="LogoImage" />
                </Flex>
              )}
              <Box>
                <Flex justifyContent="center">
                  <Box bg="blue.20" borderRadius={30} p="5px">
                    <Button
                      borderRadius={30}
                      height="2.2rem"
                      lineHeight="1"
                      colorScheme="blue"
                      variant={activeTab === ActiveTabEnum.LOGIN ? 'solid' : 'ghost'}
                      onClick={() => setActiveTab(ActiveTabEnum.LOGIN)}
                      data-testid="LoginTabBtn"
                    >
                      {t('login')}
                    </Button>
                    <Button
                      borderRadius={30}
                      height="2.2rem"
                      lineHeight="1"
                      colorScheme="blue"
                      variant={activeTab === ActiveTabEnum.SIGN_UP ? 'solid' : 'ghost'}
                      onClick={() => setActiveTab(ActiveTabEnum.SIGN_UP)}
                      data-testid="SignUpTabBtn"
                    >
                      {t('signUpLabel')}
                    </Button>
                  </Box>
                </Flex>
                {authenticationStatus === AuthenticationStatus.NEW_USER || activeTab === ActiveTabEnum.SIGN_UP ? (
                  <QASignUpView isSMB={currentPath === PATH_SMB_LOGIN_QA} />
                ) : (
                  <QALoginForm activeTab={activeTab} isSMB={currentPath === PATH_SMB_LOGIN_QA} />
                )}
              </Box>
            </Flex>
            {currentPath === PATH_SMB_LOGIN_QA && (
              <Box width="65%">
                <CarouselView />
              </Box>
            )}
          </Flex>
        </Flex>
      </MotionBox>
      {renderInvalidUserErrorMessage()}
      {renderSeekerExistsMessage()}
    </Fragment>
  );
};
